import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db, isConfigured } from './firebase';
import { track } from './analytics';

/**
 * Crash reporting. Firebase has no Crashlytics for the web, so uncaught errors
 * go two places: an `exception` event in Analytics (counts and trends), and a
 * document in the write-only `/errors` collection (the stack, the screen, the
 * browser) — read those in the Firebase console.
 */

declare const __BUILD_TIME__: string;

const MAX_PER_PAGE_LOAD = 10;
const seen = new Set<string>();

// Browser noise that says nothing about the app.
const IGNORED = [
  /^Script error\.?$/,                  // cross-origin script with no details
  /ResizeObserver loop/,                // benign layout warning
  /^AbortError/                         // a share sheet the user dismissed
];

export function reportError(error: unknown, source: 'window' | 'promise' | 'render', detail = ''): void {
  const err = error instanceof Error ? error : new Error(describe(error));
  const message = `${err.name}: ${err.message}`;
  if (IGNORED.some((re) => re.test(err.message) || re.test(message))) return;

  const key = message + (err.stack ?? '').split('\n')[1];
  if (seen.has(key) || seen.size >= MAX_PER_PAGE_LOAD) return;
  seen.add(key);

  track('exception', { description: message.slice(0, 100), fatal: source === 'render' });

  const user = isConfigured ? auth.currentUser : null;
  if (!user) return;   // the rules only accept reports from signed-in users
  void addDoc(collection(db, 'errors'), {
    message: message.slice(0, 500),
    stack: (err.stack ?? '').slice(0, 4000),
    source,
    detail: detail.slice(0, 2000),
    path: location.pathname.slice(0, 200),
    userAgent: navigator.userAgent.slice(0, 300),
    build: __BUILD_TIME__,
    uid: user.uid,
    createdAt: serverTimestamp()
  }).catch(() => { /* reporting must never throw */ });
}

export function installErrorReporting(): void {
  window.addEventListener('error', (e) => reportError(e.error ?? e.message, 'window'));
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason, 'promise'));
}

function describe(v: unknown): string {
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v) ?? String(v); } catch { return String(v); }
}
